// RU-EQUITYRS (ER-6): the Analytics equity section in rupees from booked (realised) paper P&L.
// No base capital and no percentages: the curve is cumulative booked ₹ per IST close date.
// Money uses RU-UIMOBILE's lib/money convention (UM-1: ₹-1,000.00, compact from 1 lakh, exact on tap).
import Money from "@/components/Money";
import { formatInrCompact } from "@/lib/money";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-10-02" -> "2 Oct" (read from the string itself: the date is already an IST calendar date).
export function fmtDay(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const [, m, d] = iso.split("-").map(Number);
  if (!m || !d) return "—";
  return `${d} ${MONTHS[m - 1]}`;
}

// The curve's own lowest / highest cumulative ₹ (the labels), not the axis bounds, which also include 0.
export function curveRange(points) {
  const vals = (points || []).map((p) => Number(p.cum_pnl) || 0);
  if (!vals.length) return { min: null, max: null };
  return { min: Math.min(...vals), max: Math.max(...vals) };
}

// Day number of an ISO calendar date (UTC midnight; only differences are used).
export function dayIndex(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
}

// x position by date, not by array index: a gap in the dates stays a gap.
export function chartCoords(points, W = 600, H = 120, PAD = 10) {
  if (!points || !points.length) return [];
  const vals = points.map((p) => Number(p.cum_pnl) || 0);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const range = max - min || 1;
  const x0 = dayIndex(points[0].date);
  const span = dayIndex(points[points.length - 1].date) - x0 || 1;
  return points.map((p, i) => ({
    date: p.date,
    x: PAD + ((dayIndex(p.date) - x0) / span) * (W - PAD * 2),
    y: PAD + (1 - (vals[i] - min) / range) * (H - PAD * 2),
  }));
}

export function BookedCurveChart({ points }) {
  if (!points || !points.length) return null;
  const W = 600, H = 120, PAD = 10;
  const coords = chartCoords(points, W, H, PAD);
  const vals = points.map((p) => Number(p.cum_pnl) || 0);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const zeroY = PAD + (1 - (0 - min) / (max - min || 1)) * (H - PAD * 2);
  // Step line: the booked total holds flat until the next close.
  const pathD = coords
    .map((c, i) => (i === 0 ? `M${c.x.toFixed(1)},${c.y.toFixed(1)}` : `H${c.x.toFixed(1)} V${c.y.toFixed(1)}`))
    .join(" ");
  const curve = curveRange(points);
  const last = vals[vals.length - 1];
  const stroke = last >= 0 ? "#22c55e" : "#ef4444";

  return (
    <div data-testid="booked-chart">
      <div className="flex justify-between text-[10px] text-zinc-500 font-mono-data">
        <span data-testid="booked-chart-max">max {formatInrCompact(curve.max)}</span>
        <span>Cumulative booked P&L (₹)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28" role="img" aria-label="Cumulative booked P&L in rupees by IST date">
        <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} stroke="#3f3f46" strokeDasharray="4 4" />
        <path data-testid="booked-chart-path" d={pathD} fill="none" stroke={stroke} strokeWidth="2" />
      </svg>
      <div className="flex justify-between text-[10px] text-zinc-500 font-mono-data">
        <span data-testid="booked-chart-from">{fmtDay(points[0].date)}</span>
        <span data-testid="booked-chart-min">min {formatInrCompact(curve.min)}</span>
        <span data-testid="booked-chart-to">{fmtDay(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
}

function istTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} ${time} IST`;
}

// RU-FU27 FU-3: `open.unknown` counts the positions not valued by sign: those at their last real mark (also in
// `stale_count`, valued in `open.pnl` as an estimate) and those without a mark (not in `open.pnl`).
export function openCaveats(open) {
  if (!open || !open.available) return [];
  const stale = Math.max(0, Number(open.stale_count) || 0);
  const unvalued = Math.max(0, (Number(open.unknown) || 0) - stale);
  const out = [];
  if (unvalued > 0) out.push(`${unvalued} without a mark, not valued`);
  if (stale > 0) out.push(`${stale} at the last real mark (estimate)`);
  return out;
}

export function OpenPnlLine({ open, since }) {
  const ok = open && open.available;
  const when = istTime(since);
  const caveats = openCaveats(open);
  return (
    <div className="mt-3 space-y-0.5 text-xs text-zinc-500">
      <div data-testid="booked-open-line">
        Open paper positions (unrealised, marks may be delayed; not in the curve):{" "}
        {ok ? (
          <Money value={open.pnl} plus testid="booked-open-pnl"
                 className={`font-mono-data ${open.pnl >= 0 ? "text-green-400" : "text-red-400"}`} />
        ) : (
          <span className="text-zinc-400">unavailable</span>
        )}
        {caveats.length > 0 && <span data-testid="booked-open-unknown"> · {caveats.join(" · ")}</span>}
      </div>
      <div data-testid="booked-note">
        Booked (realised) paper P&L by IST close date, gross before charges; live broker P&L not included.
        {when && ` First day counts closes since ${when}.`}
      </div>
    </div>
  );
}
